import React, {useContext, useEffect, useState} from 'react';
import {calculateDistance, drawCanvas, formatLatLng, moveMapEl, usePrevious} from '../../common/helper';
import {useDispatch, useSelector} from 'react-redux';
import {
    circlesByID, currentCircleDevices, currentCircleUsers, currentCircleUsersAll,
    locationByID,
    ORMCircles,
    ORMDevices,
    ORMLocations,
    ORMUsers,
    usersByID
} from '../../app/models/ormSelectors';
import {
    selectActiveCircle,
    selectActiveUser,
    setActiveUser
} from '../dashboard/dashboardSlice';
import {MapContext} from './MapContext';
import ShowBtn from '../dashboard/ShowBtn';
import {selectUserInfo} from '../login/loginSlice';
import {selectPseudoFlow} from '../onboarding/onboardingSlice';
import {setPopupShow} from './mapSlice';


const Markers = () => {
    const map = useContext(MapContext).googleMap;
    const initLat = useContext(MapContext).lat;
    const initLng = useContext(MapContext).lng;
    const dispatch = useDispatch();
    const geoByID = useSelector(locationByID);
    const userByID = useSelector(usersByID);
    const activeUser = useSelector(selectActiveUser);
    const [markers, setMarkers] = useState([]);
    const [bounds, setBounds] = useState(null);
    const ormUsers = useSelector(ORMUsers);
    const ormCircles = useSelector(ORMCircles);
    const ormLocations = useSelector(ORMLocations);
    const prevActiveUser = usePrevious(activeUser);
    const activeCircle = useSelector(selectActiveCircle);
    const activeUserPrevStatuses = usePrevious(geoByID(activeUser).permissionsIssues);
    const accountOwner = useSelector(selectUserInfo);
    const initCoords = formatLatLng(initLat, initLng);
    const isPseudoFlow = useSelector(selectPseudoFlow);
    const currCircleAllIDs = useSelector(currentCircleUsersAll);


    const onShowAllClick = () => map.fitBounds(bounds, 70);

    const updateMarkerIcons = user => {
        const userLocationStatus = geoByID(user.id).locationStatus;
        const userPermissionsIssues = geoByID(user.id).permissionsIssues;
        const isUserActive = user.id === activeUser;
        const markerToUpdate = markers.find(marker => user.id === marker?.id);

        const status = userPermissionsIssues || userLocationStatus || 'PERMISSIONS';

        const setIcon = icon => markerToUpdate.setIcon(
            {
                url: icon,
                scaledSize: new window.google.maps.Size(130, 130),
                optimized: true
            }
        );

        markerToUpdate && drawCanvas(user.photo_url || '../images/default-static-avatar.png', user.deviceIcon, isUserActive, status, setIcon);
    };

    useEffect(() => {
        if (!isPseudoFlow) {

            const pins = ormUsers.map(i => {
                return new window.google.maps.Marker({
                    draggable: false,
                    animation: window.google.maps.Animation.DROP,
                    id: i.id,
                    icon: 'https://maps.gstatic.com/mapfiles/transparent.png',
                    map: map
                });
            }).filter(i => i !== null);

            setMarkers(prevState => prevState.concat(pins));
        }

    }, [ormUsers, isPseudoFlow]);

    useEffect(() => {
        if (!isPseudoFlow) {
            const mapBounds = new google.maps.LatLngBounds();

            currCircleAllIDs.forEach(user => {
                const userGeo = geoByID(user);


                if (userGeo.location) {
                    const userLatLng = formatLatLng(userGeo.location?.latitude, userGeo.location?.longitude);
                    mapBounds.extend(userLatLng);
                }
            });

            setBounds(mapBounds);
        }
    }, [activeCircle, isPseudoFlow, ormUsers, ormLocations]);

    useEffect(() => {
        if (markers.length && activeUser) {
            ormUsers.forEach(item => {
                updateMarkerIcons(item);
            });
        }
    }, [activeUser, markers, ormUsers]);

    useEffect(() => {
        if (markers.length) {
            if (activeUserPrevStatuses !== geoByID(activeUser).permissionsIssues) {
                updateMarkerIcons(userByID(activeUser));
            }
        }

    }, [ormLocations, markers]);

    useEffect(() => {
        if (markers.length) {
            markers.forEach(marker => {
                const location = geoByID(marker.id).location;
                const latLng = location && formatLatLng(location.latitude, location.longitude);
                const prevLatLng = marker.getPosition();
                const callback = (coords) => marker.setPosition(coords);

                if (!location && marker.id === accountOwner.id) {
                    return marker.setPosition(initCoords);
                }

                if (!prevLatLng) {
                    location && marker.setPosition(latLng);
                }

                if (prevLatLng) {
                    const prevLat = prevLatLng.lat();
                    const prevLng = prevLatLng.lng();
                    const distance = calculateDistance(prevLat, prevLng, location.latitude, location.longitude);
                    if (distance === 0 || distance > 500) {
                        return marker.setPosition(latLng);
                    } else {
                        moveMapEl(location.latitude, location.longitude, prevLat, prevLng, callback);
                    }
                }
            })
        }

    }, [markers, ormLocations]);

    useEffect(() => {

        if (markers.length) {
            markers.forEach((marker) => {
                marker.addListener('click', () => {
                    dispatch(setPopupShow(true));
                    dispatch(setActiveUser(marker.id));
                })
            })
        }

    }, [markers]);

    useEffect(() => {
        if (map && markers.length && ormUsers) {

            markers.forEach(marker => {
                if (!currCircleAllIDs.includes(marker.id)) {
                    return marker.setMap(null);
                }
                marker.setMap(map);
            })
        }

    }, [map, markers, activeCircle, ormUsers, ormCircles]);

    useEffect(() => {
        if (prevActiveUser && activeUser) {
            const prevUser = userByID(prevActiveUser);
            const selectedUser = userByID(activeUser);

            if (prevUser !== selectedUser) {
                updateMarkerIcons(prevUser);
                updateMarkerIcons(selectedUser);
            }

        }

    }, [prevActiveUser, activeUser]);


    useEffect(() => {
        if (!isPseudoFlow) {
            if (activeUser) {
                if (geoByID(activeUser).location) {
                    const selectedUserCoords = geoByID(activeUser).location;
                    const latLng = formatLatLng(selectedUserCoords.latitude, selectedUserCoords.longitude);
                    map.panTo(latLng);
                    map.panBy(0, -70);
                } else {
                    map.panTo(initCoords);
                }
            }
        }
    }, [activeUser, geoByID(activeUser).location, isPseudoFlow]);


    return (
        <>
            <ShowBtn show={!isPseudoFlow} onClick={onShowAllClick}/>
        </>
    );
};

export default Markers;